import { type HTMLAttributes, type ReactNode } from "react"
import { twMerge } from "tailwind-merge"
import { type IconProps } from "../../icon/Icon"

type Size = "small" | "medium" | "large"

const HeadingText = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) => {
  return (
    <h3 className={twMerge("tt-heading-text", className)} {...props}>
      {children}
    </h3>
  )
}

const BodyText = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => {
  return (
    <p className={twMerge("tt-body-text", className)} {...props}>
      {children}
    </p>
  )
}

const ActionsRow = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={twMerge("flex flex-row gap-2", className)} {...props}>
      {children}
    </div>
  )
}

const EmptyStateIcon = ({
  as: IconComponent,
  className,
  ...props
}: IconProps) => {
  return <IconComponent className={twMerge("tt-icon", className)} {...props} />
}

type EmptyStateStackProps = HTMLAttributes<HTMLDivElement> & {
  size?: Size
}

export const EmptyStateStack = ({
  className,
  size = "medium",
  ...props
}: EmptyStateStackProps) => {
  return (
    <div
      className={twMerge(
        "flex flex-col gap-5 items-center justify-center text-center p-16",
        className
      )}
      data-size={size}
      {...props}
    />
  )
}

const BodyStack = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={twMerge("flex flex-col gap-1", className)} {...props} />
  )
}

export type EmptyStateProps = {
  actions?: ReactNode
  badge?: ReactNode
  bodyText: string
  headingText?: string
  icon?: IconProps["as"]
  size?: Size
  className?: string
}

/**
 * A component to use when there is no content to display. Helpful for prompting
 * users to take get started, showing that a search returned no results, or
 * indicating that a page is empty.
 *
 * Can be used directly, or composed using
 * `EmptyStateStack` and other subcomponents.
 *
 * ```tsx
 * import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
 * import { MagnifyingGlass } from "@phosphor-icons/react"
 *
 * <EmptyState
 *   headingText="No results found"
 *   bodyText="Try adjusting your filters or searching for something else."
 *   icon={MagnifyingGlass}
 * />
 * ```
 */

export const EmptyState = ({
  actions,
  badge,
  bodyText,
  headingText,
  icon,
  size = "medium",
  className,
}: EmptyStateProps) => {
  return (
    <EmptyStateStack size={size} className={className}>
      {badge}
      {icon && <EmptyStateIcon as={icon} />}
      <BodyStack>
        {headingText && <HeadingText>{headingText}</HeadingText>}
        <BodyText>{bodyText}</BodyText>
      </BodyStack>
      {actions && <ActionsRow>{actions}</ActionsRow>}
    </EmptyStateStack>
  )
}

EmptyState.Stack = EmptyStateStack
EmptyState.ActionsRow = ActionsRow
EmptyState.HeadingText = HeadingText
EmptyState.BodyStack = BodyStack
EmptyState.BodyText = BodyText
EmptyState.Icon = EmptyStateIcon
